import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const PherAakaraniPanchnamaForm = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'PherAakarani_Panchnama.pdf' : 'Reassessment_Panchnama.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          .page {
            width: 210mm;
            height: 297mm;
            padding: 20mm;
            margin: 0 auto 20px auto;
            box-sizing: border-box;
            position: relative;
            background-color: white;
            overflow: hidden;
            box-shadow: 0 0 15px rgba(0,0,0,0.1);
          }
          
          .dashed-line {
            border-bottom: 1px dashed black;
            display: inline-block;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          th, td {
            border: 1px solid black;
            padding: 8px;
            vertical-align: top;
          }

          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 210mm !important;
              height: 297mm !important;
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .page {
              margin: 0 !important;
              box-shadow: none !important;
              width: 210mm !important;
              height: 297mm !important;
              page-break-after: always !important;
              break-after: page !important;
              break-inside: avoid !important;
              border: none !important;
            }
            .no-print {
              display: none !important;
            }
            .min-h-screen {
              min-height: auto !important;
              padding: 0 !important;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4 no-print">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div ref={componentRef} className="flex flex-col items-center text-black">
        {/* PAGE 1 */}
        <div className="bg-white relative box-border flex flex-col page text-[16px]">
            <div className="text-center mb-6">
               <p className="font-bold text-[18px]">{language === 'marathi' ? 'महाराष्ट्र ग्रामपंचायत कर व फी (सुधारणा) नियम २०१५ नुसार' : 'As per Maharashtra Gram Panchayat Tax and Fee (Amendment) Rules 2015'}</p>
               <p className="font-bold text-[20px] underline mt-1">{language === 'marathi' ? 'कर आकारणी समिती- मालमत्ता तपासणी/पाहणी अहवाल/पंचनामा' : 'Tax Assessment Committee - Property Inspection/Survey Report/Panchnama'}</p>
            </div>

            <div className="mb-6 leading-relaxed">
               <p className="mb-2">{language === 'marathi' ? 'दिनांक ...../....../२०' : 'Date ...../....../20'}</p>
               <p className="flex flex-wrap items-end">
                  <span>{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                  <span className="dashed-line flex-grow h-4 mx-2"></span>
                  <span>{language === 'marathi' ? 'ता.' : 'Tal.'}</span>
                  <span className="dashed-line w-40 h-4 mx-2"></span>
               </p>
               <p className="flex flex-wrap items-end mt-2">
                  <span className="dashed-line w-24 h-4 mr-1"></span>
                  <span>{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
                  <span className="dashed-line flex-grow h-4 mx-2"></span>
                  <span>{language === 'marathi' ? 'येथील कर आकारणी समितीने' : 'Tax Assessment Committee here'}</span>
               </p>
               <p className="mt-2">{language === 'marathi' ? 'कर व फी यांची फेरआकारणी यादीच्या अनुषंगाने पुढील मालमत्तेची' : 'In accordance with the reassessment list of taxes and fees, the following property was'}</p>
               <p>{language === 'marathi' ? 'तपासणी/ पाहणी केली.' : 'inspected / surveyed.'}</p>
            </div>

            <table className="mb-4 text-[15px] w-full" style={{ tableLayout: 'fixed' }}>
               <tbody>
                  <tr>
                     <td style={{ width: '8%' }} className="text-center">{language === 'marathi' ? '१.' : '1.'}</td>
                     <td style={{ width: '32%' }}>{language === 'marathi' ? 'इमारतीचा/जमिनीचा अनुक्रमांक' : 'Serial Number of Building/Land'}</td>
                     <td colSpan="3" style={{ width: '60%' }}></td>
                  </tr>
                  <tr>
                     <td className="text-center">{language === 'marathi' ? '२.' : '2.'}</td>
                     <td>{language === 'marathi' ? 'मालमत्ता क्रमांक' : 'Property Number'}</td>
                     <td colSpan="3"></td>
                  </tr>
                  <tr>
                     <td className="text-center">{language === 'marathi' ? '३.' : '3.'}</td>
                     <td>{language === 'marathi' ? 'मालकाचे नाव :' : 'Name of Owner :'}</td>
                     <td colSpan="3"></td>
                  </tr>
                  <tr>
                     <td className="text-center">{language === 'marathi' ? '४.' : '4.'}</td>
                     <td>{language === 'marathi' ? (
                       <>ताब्यात असणा-याचे<br/>नाव :<br/>(भोगवटादाराचे<br/>नाव)</>
                     ) : (
                       <>Name of person<br/>in possession :<br/>(Name of<br/>Occupier)</>
                     )}</td>
                     <td colSpan="3"></td>
                  </tr>
                  <tr>
                     <td className="text-center" rowSpan="6">{language === 'marathi' ? '५.' : '5.'}</td>
                     <td className="font-bold">{language === 'marathi' ? 'इमारतीचा प्रकार' : 'Type of Building'}</td>
                     <td style={{ width: '20%' }} className="font-bold text-center">{language === 'marathi' ? (
                       <>लांबी X<br/>रुंदी (चौ.<br/>फुट)</>
                     ) : (
                       <>Length X<br/>Width (Sq.<br/>Ft.)</>
                     )}</td>
                     <td style={{ width: '15%' }} className="font-bold text-center">{language === 'marathi' ? (
                       <>क्षेत्रफळ<br/>(चौ.मीटर)</>
                     ) : (
                       <>Area<br/>(Sq.Meter)</>
                     )}</td>
                     <td style={{ width: '25%' }} className="font-bold">{language === 'marathi' ? (
                       <>इमारतीचा<br/>वापर<br/><span className="font-normal text-[14px]">१) निवासी<br/>२) वाणिज्यिक<br/>३) औद्योगिक</span></>
                     ) : (
                       <>Use of<br/>Building<br/><span className="font-normal text-[14px]">1) Residential<br/>2) Commercial<br/>3) Industrial</span></>
                     )}</td>
                  </tr>
                  <tr>
                     <td>{language === 'marathi' ? '१.झोपडी किंवा मातीचे घर' : '1. Hut or mud house'}</td>
                     <td></td>
                     <td></td>
                     <td></td>
                  </tr>
                  <tr>
                     <td>{language === 'marathi' ? '२.दगड विटांचे मातीचे घर' : '2. Stone and brick mud house'}</td>
                     <td></td>
                     <td></td>
                     <td></td>
                  </tr>
                  <tr>
                     <td>{language === 'marathi' ? (
                       <>३.दगड विटांचे चुना<br/>किंवा सिमेंट्‌चे पक्केघर</>
                     ) : (
                       <>3. Stone and brick lime<br/>or cement pucca house</>
                     )}</td>
                     <td></td>
                     <td></td>
                     <td></td>
                  </tr>
                  <tr>
                     <td>{language === 'marathi' ? '४.आरसीसी पध्दतीचे घर' : '4. RCC type house'}</td>
                     <td></td>
                     <td></td>
                     <td></td>
                  </tr>
                  <tr>
                     <td>{language === 'marathi' ? '५.सहान जागा' : '5. Courtyard space'}</td>
                     <td></td>
                     <td></td>
                     <td></td>
                  </tr>
               </tbody>
            </table>

            <div className="absolute bottom-8 left-14 text-blue-700 text-[12px] underline print:hidden border-t-0">
              www.majhigrampanchayat.com
            </div>
        </div>

        {/* PAGE 2 */}
        <div className="bg-white relative box-border flex flex-col page text-[16px]">
            <table className="mb-4 text-[15px] mt-4 w-full" style={{ tableLayout: 'fixed' }}>
               <tbody>
                  <tr>
                     <td style={{ width: '8%' }} className="text-center" rowSpan="6">{language === 'marathi' ? '६.' : '6.'}</td>
                     <td style={{ width: '32%' }} className="font-bold">{language === 'marathi' ? 'इमारतीचा प्रकार' : 'Type of Building'}</td>
                     <td style={{ width: '20%' }} className="font-bold text-center">{language === 'marathi' ? (
                       <>बांधकाम<br/>वर्ष</>
                     ) : (
                       <>Year of<br/>Construction</>
                     )}</td>
                     <td style={{ width: '15%' }} className="font-bold text-center">{language === 'marathi' ? 'वयोमान' : 'Age/Lifespan'}</td>
                     <td style={{ width: '25%' }} className="font-bold text-center">{language === 'marathi' ? (
                       <>आकारण्यात<br/>आलेली<br/>कराची<br/>रक्कम</>
                     ) : (
                       <>Amount<br/>of Tax<br/>Assessed</>
                     )}</td>
                  </tr>
                  <tr>
                     <td>{language === 'marathi' ? '१.झोपडी किंवा मातीचे घर' : '1. Hut or mud house'}</td>
                     <td></td>
                     <td></td>
                     <td></td>
                  </tr>
                  <tr>
                     <td>{language === 'marathi' ? '२.दगड विटांचे मातीचे घर' : '2. Stone and brick mud house'}</td>
                     <td></td>
                     <td></td>
                     <td></td>
                  </tr>
                  <tr>
                     <td>{language === 'marathi' ? (
                       <>३.दगड विटांचे चुना<br/>किंवा सिमेंट्‌चे पक्केघर</>
                     ) : (
                       <>3. Stone and brick lime<br/>or cement pucca house</>
                     )}</td>
                     <td></td>
                     <td></td>
                     <td></td>
                  </tr>
                  <tr>
                     <td>{language === 'marathi' ? '४.आरसीसी पध्दतीचे घर' : '4. RCC type house'}</td>
                     <td></td>
                     <td></td>
                     <td></td>
                  </tr>
                  <tr>
                     <td>{language === 'marathi' ? '५.सहान जागा' : '5. Courtyard space'}</td>
                     <td></td>
                     <td></td>
                     <td></td>
                  </tr>
               </tbody>
            </table>

            <table className="mb-4 text-[15px] w-full" style={{ tableLayout: 'fixed' }}>
               <tbody>
                  <tr>
                     <td style={{ width: '8%' }} className="text-center" rowSpan="4">{language === 'marathi' ? '७.' : '7.'}</td>
                     <td style={{ width: '20%' }} rowSpan="4">{language === 'marathi' ? (
                       <>जागेची<br/>चतु:र्सीमा:<br/><span className="text-[13px]">(वैकल्पिक)</span></>
                     ) : (
                       <>Boundaries<br/>of place:<br/><span className="text-[13px]">(Optional)</span></>
                     )}</td>
                     <td style={{ width: '15%' }} className="text-center">{language === 'marathi' ? 'पूर्वेस' : 'East'}</td>
                     <td style={{ width: '57%' }}></td>
                  </tr>
                  <tr>
                     <td className="text-center">{language === 'marathi' ? 'पश्चिमेस' : 'West'}</td>
                     <td></td>
                  </tr>
                  <tr>
                     <td className="text-center">{language === 'marathi' ? 'दक्षिणेस' : 'South'}</td>
                     <td></td>
                  </tr>
                  <tr>
                     <td className="text-center">{language === 'marathi' ? 'उत्तरेस' : 'North'}</td>
                     <td></td>
                  </tr>
               </tbody>
            </table>

            <table className="text-[15px] w-full mt-4" style={{ tableLayout: 'fixed' }}>
               <thead>
                  <tr>
                     <th style={{ width: '8%' }} className="text-center">{language === 'marathi' ? 'अ.क्र' : 'Sr.No'}</th>
                     <th style={{ width: '36%' }}>{language === 'marathi' ? 'कर आकारणी समिती सदस्यांची नावे' : 'Names of Tax Assessment Committee Members'}</th>
                     <th style={{ width: '26%' }}></th>
                     <th style={{ width: '15%' }}>{language === 'marathi' ? 'पद' : 'Designation'}</th>
                     <th style={{ width: '15%' }}>{language === 'marathi' ? 'स्वाक्षरी' : 'Signature'}</th>
                  </tr>
               </thead>
               <tbody>
                  <tr>
                     <td className="text-center h-10">{language === 'marathi' ? '१.' : '1.'}</td>
                     <td></td>
                     <td>{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</td>
                     <td>{language === 'marathi' ? 'अध्यक्ष' : 'President'}</td>
                     <td></td>
                  </tr>
                  <tr>
                     <td className="text-center h-10">{language === 'marathi' ? '२.' : '2.'}</td>
                     <td></td>
                     <td>{language === 'marathi' ? 'उपसरपंच' : 'Deputy Sarpanch'}</td>
                     <td>{language === 'marathi' ? 'सदस्य' : 'Member'}</td>
                     <td></td>
                  </tr>
                  <tr>
                     <td className="text-center h-12">{language === 'marathi' ? '३.' : '3.'}</td>
                     <td></td>
                     <td>{language === 'marathi' ? (
                       <>विस्तार<br/>अधिकारी(पंचायत)</>
                     ) : (
                       <>Extension<br/>Officer (Panchayat)</>
                     )}</td>
                     <td>{language === 'marathi' ? 'सदस्य' : 'Member'}</td>
                     <td></td>
                  </tr>
                  <tr>
                     <td className="text-center h-16">{language === 'marathi' ? '४.' : '4.'}</td>
                     <td></td>
                     <td>{language === 'marathi' ? (
                       <>शाखा/कनिष्ठ<br/>अभियंता<br/>जि.प.(बांधकामे)</>
                     ) : (
                       <>Branch/Junior<br/>Engineer<br/>Z.P. (Construction)</>
                     )}</td>
                     <td>{language === 'marathi' ? 'सदस्य' : 'Member'}</td>
                     <td></td>
                  </tr>
                  <tr>
                     <td className="text-center h-16">{language === 'marathi' ? '५.' : '5.'}</td>
                     <td></td>
                     <td>{language === 'marathi' ? (
                       <>ग्रामविकास<br/>अधिकारी/ ग्रामसेवक<br/>(पंचायतीचा सचिव)</>
                     ) : (
                       <>Village Development<br/>Officer/Gram Sevak<br/>(Secretary of Panchayat)</>
                     )}</td>
                     <td>{language === 'marathi' ? (
                       <>सदस्य-<br/>सचिव</>
                     ) : (
                       <>Member-<br/>Secretary</>
                     )}</td>
                     <td></td>
                  </tr>
               </tbody>
            </table>

            <div className="absolute bottom-8 left-14 text-blue-700 text-[12px] underline print:hidden border-t-0">
              www.majhigrampanchayat.com
            </div>
        </div>
      </div>
    </div>
  );
};

export default PherAakaraniPanchnamaForm;
